import { Link } from "react-router-dom";
import Section from "../../components/ui/Section";

const Products = () => {
  return (
    <div className="bg-bg">
      <section className="pt-12 pb-14 sm:pt-16 sm:pb-16 border-b border-line">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="kicker mb-5">NOVTIA Products</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-ink mb-5 leading-[1.06]">
              A portfolio of governed AI capabilities.
            </h1>
            <p className="text-sm sm:text-base text-muted mb-7 max-w-2xl">
              One product is in the field today — AIRG, a runtime governor for
              AI agents — with further capabilities still in development. Each
              is built on the NOVTIA Sovereign Core, so control, evidence, and
              orchestration are shared rather than rebuilt for every system.
            </p>
            <Link
              to="/products/airg"
              className="inline-flex items-center bg-signal px-5 py-2.5 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors"
            >
              Explore AIRG
            </Link>
          </div>
        </div>
      </section>

      {/* AIRG */}
      <section className="border-b border-line">
        <Section eyebrow="Flagship product" title="AIRG — AI Runtime Governor">
          <div className="grid md:grid-cols-[1.15fr,0.85fr] gap-8 lg:gap-12 items-start">
            <div className="space-y-5">
              <p className="text-base sm:text-lg text-ink leading-relaxed">
                A runtime control plane that sits inside an agent&apos;s
                execution path. Every tool call, API call, and model call is
                evaluated against policy, risk, budget, and identity — and
                returns allow, review, or block before the action reaches a
                real system.
              </p>
              <ul className="space-y-2.5 text-sm text-muted">
                <li className="flex gap-2.5">
                  <span className="mt-1.5 h-1 w-1 flex-none bg-signal" />
                  <span>Dual gateway — covers what agents say (LLM calls) and what they do (tool/API calls).</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="mt-1.5 h-1 w-1 flex-none bg-signal" />
                  <span>Signed, hash-chained receipt on every verdict, structured to support audit and governance review.</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="mt-1.5 h-1 w-1 flex-none bg-signal" />
                  <span>SURGE risk-priced governance — riskier actions cost more against a budget, and exhausted agents stop automatically.</span>
                </li>
              </ul>
              <Link
                to="/products/airg"
                className="inline-flex items-center text-sm font-semibold text-signal hover:underline"
              >
                See how AIRG works →
              </Link>
            </div>
            <div className="surface-card p-6">
              <p className="kicker mb-3">Where it stands</p>
              <span className="status-pill inline-block mb-5">
                Live with design partners
              </span>
              <dl className="space-y-4 text-sm">
                <div>
                  <dt className="text-ink font-semibold mb-1">Deployment</dt>
                  <dd className="text-muted">
                    Governing real agent actions in controlled environments
                    with a small number of partners.
                  </dd>
                </div>
                <div>
                  <dt className="text-ink font-semibold mb-1">Integration</dt>
                  <dd className="text-muted">
                    Python SDK, MCP server, or direct gateway integration.
                  </dd>
                </div>
              </dl>
              <a
                href="https://app.airg.nov-tia.com"
                target="_blank"
                rel="noreferrer"
                className="mt-5 pt-4 border-t border-line inline-flex items-center bg-signal px-4 py-2 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors"
              >
                Open AIRG console
              </a>
            </div>
          </div>
          <figure className="surface-card overflow-hidden mt-12 max-w-5xl mx-auto">
            <img
              src="/airg-control-plane-refined.png"
              alt="Illustrative view of the AIRG control plane evaluating agent actions between real-world systems and AI systems"
              className="block w-full h-auto"
            />
          </figure>
        </Section>
      </section>

      {/* BEYOND THE FLAGSHIP */}
      <section>
        <Section
          eyebrow="Beyond the flagship"
          title="Not every problem is solved by an off-the-shelf product."
          description="Alongside AIRG, NOVTIA builds governed systems around an organisation's own environment, and continues to develop the capabilities that become future products."
        >
          <div className="grid md:grid-cols-2 gap-5">
            <div className="surface-card p-6 flex flex-col">
              <p className="kicker mb-3">Tailored delivery</p>
              <h3 className="font-display text-lg font-semibold text-ink mb-2">
                Services, shaped around your operating environment.
              </h3>
              <p className="text-sm text-muted mb-5">
                From strategy and process design to engineering, integration,
                and operational governance — how NOVTIA makes AI dependable in
                the context you already run.
              </p>
              <Link
                to="/services"
                className="mt-auto text-sm font-semibold text-signal hover:underline"
              >
                Explore services →
              </Link>
            </div>

            <div className="surface-card p-6 flex flex-col">
              <p className="kicker mb-3">In development</p>
              <h3 className="font-display text-lg font-semibold text-ink mb-2">
                More products are in the pipeline.
              </h3>
              <p className="text-sm text-muted mb-5">
                NOVTIA Labs develops reusable capabilities that may become
                future products. AIRG is live with design partners; the rest are
                earlier-stage and not yet public.
              </p>
              <Link
                to="/labs"
                className="mt-auto text-sm font-semibold text-signal hover:underline"
              >
                Inside NOVTIA Labs →
              </Link>
            </div>
          </div>
        </Section>
      </section>

      {/* CLOSE */}
      <section className="border-t border-line">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <div className="grid lg:grid-cols-[1.1fr,0.9fr] gap-8 items-center">
            <div>
              <p className="kicker mb-4">Next step</p>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-ink leading-[1.1]">
                Tell us where agents are getting close to something
                consequential.
              </h2>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center bg-signal px-5 py-2.5 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors"
              >
                Start a conversation
              </Link>
              <Link
                to="/case-studies"
                className="inline-flex items-center border border-line-strong px-5 py-2.5 text-sm text-ink hover:border-signal hover:text-signal transition-colors"
              >
                See the work
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
